import axios from "axios";
import axiosRetry from "axios-retry";
import { getToken } from "./auth";

const API_URL = process.env.REACT_APP_API_BASE_URL;

// Axios instance with base URL and retries
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 20000, // 20s timeout to prevent hanging
});

// Configure retries for transient errors (e.g., 429, 503, network errors)
axiosRetry(axiosInstance, {
  retries: 3,
  retryDelay: (retryCount) => retryCount * 1000, // Exponential backoff: 1s, 2s, 3s
  retryCondition: (error) => {
    // Retry on network errors or specific HTTP status codes
    return (
      axios.isAxiosError(error) &&
      (!error.response || [429, 503].includes(error.response.status))
    );
  },
});

// Automatically attach JWT token
axiosInstance.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    console.warn("No JWT token found, API calls may fail with 401/403");
  }
  return config;
});

// Handle token expiration
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Clear auth data
      const Cookies = require("js-cookie").default;
      Cookies.remove("auth_token");
      Cookies.remove("user");
      // Redirect to login
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

/**
 * Generic helper to safely return response data
 */
const safeReturn = async (apiCall, errorMessage) => {
  try {
    const res = await apiCall();
    return res.data;
  } catch (err) {
    console.error(errorMessage, err);
    throw err;
  }
};

/**
 * Get total users
 */
export const getTotalUsers = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/GetTotalUsers"),
    "Error fetching total users",
  );

/**
 * Get total orders
 */
export const getTotalOrders = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/GetTotalOrders"),
    "Error fetching total orders",
  );

/**
 * Get total items
 */
export const getTotalItems = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/GetTotalItems"),
    "Error fetching total items",
  );

/**
 * Get total completed orders
 */
export const getTotalCompletedOrders = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/GetTotalCompletedOrders"),
    "Error fetching total completed orders",
  );

/**
 * Helpers: split total completed orders
 */
export const getCompletedOrdersByPeriod = async () => {
  const { totalCompletedOrders } = await getTotalCompletedOrders();
  return {
    daily: totalCompletedOrders.daily,
    monthly: totalCompletedOrders.monthly,
    yearly: totalCompletedOrders.yearly,
  };
};

/**
 * Get users with most completed orders
 */
export const getMostCompletedOrders = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/MostCompletedOrders"),
    "Error fetching most completed orders",
  );

/**
 * Helpers: group most completed orders by period type
 */
export const getMostCompletedOrdersByPeriod = async () => {
  const { mostCompletedOrders } = await getMostCompletedOrders();

  return {
    daily: mostCompletedOrders.filter((o) => o.periodType === "Daily"),
    monthly: mostCompletedOrders.filter((o) => o.periodType === "Monthly"),
    yearly: mostCompletedOrders.filter((o) => o.periodType === "Yearly"),
  };
};

/**
 * Get average order completion time (raw response)
 */
export const getAverageOrderCompletionTimeByPeriod = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/AverageOrderCompletionTimeByPeriod"),
    "Error fetching average order completion time",
  );

/**
 * Helpers: split average completion time into daily, monthly, yearly
 */
export const getAverageCompletionTimeByPeriod = async () => {
  const { averageCompletionTime } =
    await getAverageOrderCompletionTimeByPeriod();

  return {
    daily: averageCompletionTime.daily, // {2024: [{day, count}, ...], 2025: [...]}
    monthly: averageCompletionTime.monthly, // {2024: {Jan: "00:00:00", ...}, 2025: {...}}
    yearly: averageCompletionTime.yearly, // {2024: "00:00:00", 2025: "00:03:16"}
  };
};

/**
 * Get orders statuses
 */
export const getOrdersStatuses = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/OrdersStatuses"),
    "Error fetching orders statuses",
  );

/**
 * Get compare orders by year
 */
export const getCompareOrdersByYear = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/CompareOrdersByYear"),
    "Error fetching compare orders by year",
  );

/**
 * Helpers: transform yearly comparison into grouped structure
 */
export const getCompareOrdersByYearGrouped = async () => {
  const { yearlyOrders } = await getCompareOrdersByYear();

  return yearlyOrders.reduce((acc, item) => {
    if (!acc[item.year]) acc[item.year] = [];
    acc[item.year].push({
      month: item.period,
      count: item.orderCount,
    });
    return acc;
  }, {});
};

/**
 * Get compare orders by day
 */
export const getCompareOrdersByDay = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/CompareOrdersByDay"),
    "Error fetching compare orders by day",
  );

/**
 * Helpers: transform daily comparison into grouped structure
 */
export const getCompareOrdersByDayGrouped = async () => {
  const { comparison } = await getCompareOrdersByDay();

  return comparison.reduce((acc, item) => {
    if (!acc[item.year]) acc[item.year] = [];
    acc[item.year].push({
      day: item.period,
      count: item.orderCount,
    });
    return acc;
  }, {});
};

/**
 * Get oversights
 */
export const getOversights = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/Oversights"),
    "Error fetching oversights",
  );

/**
 * Helpers: split oversights into daily, monthly, yearly
 */
export const getOversightsByPeriod = async () => {
  const { overSights } = await getOversights();

  return {
    daily: overSights.daily,
    monthly: overSights.monthly,
    yearly: overSights.yearly,
  };
};

/**
 * Get fastest and slowest orders
 */
export const getFastestAndSlowestOrders = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/FastestOrderSlowestOrder"),
    "Error fetching fastest and slowest orders",
  );

/**
 * Helpers: extract fastest & slowest orders
 */
export const getFastestAndSlowestOrdersParsed = async () => {
  const { orders } = await getFastestAndSlowestOrders();

  return {
    fastest: {
      orderNo: orders.fastestOrderNo,
      userId: orders.fastestUserId,
      timeSeconds: orders.fastestTimeTakenSeconds || 0,
    },
    slowest: {
      orderNo: orders.slowestOrderNo,
      userId: orders.slowestUserId,
      timeSeconds: orders.slowestTimeTakenSeconds || 0,
    },
    companyCode: orders.companyCode,
  };
};

/**
 * Get agents KPI by date range
 * @param {string} fromDate - ISO date string (e.g. "2025-09-01T00:00:00")
 * @param {string} toDate   - ISO date string (e.g. "2025-09-17T23:59:59")
 */
export const getAgentsKpiByDate = (fromDate, toDate) =>
  safeReturn(
    () =>
      axiosInstance.get("/Dashboard/GetAgentsKpiByDate", {
        params: { fromDate, toDate },
        responseType: "blob",
        timeout: 60000, // 60 seconds for file export operations
      }),
    "Error fetching agents KPI by date",
  );

/**
 * Get controllers KPI by date range
 * @param {string} fromDate - ISO date string (e.g. "2025-09-01T00:00:00")
 * @param {string} toDate   - ISO date string (e.g. "2025-09-17T23:59:59")
 */
export const getControllersKpiByDate = (fromDate, toDate) =>
  safeReturn(
    () =>
      axiosInstance.get("/Dashboard/GetControllersKpiByDate", {
        params: { fromDate, toDate },
        responseType: "blob",
        timeout: 60000, // 60 seconds for file export operations
      }),
    "Error fetching controllers KPI by date",
  );

/**
 * Get drivers KPI by date range
 * @param {string} fromDate - ISO date string (e.g. "2025-09-01T00:00:00")
 * @param {string} toDate   - ISO date string (e.g. "2025-09-17T23:59:59")
 */
export const getDriversKpiByDate = (fromDate, toDate) =>
  safeReturn(
    () =>
      axiosInstance.get("/Dashboard/GetDriversKpiByDate", {
        params: { fromDate, toDate },
        responseType: "blob",
        timeout: 60000, // 60 seconds for file export operations
      }),
    "Error fetching drivers KPI by date",
  );

/**
 * Get Agents KPI
 */
export const getAgentsKpi = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/AgentsKpi"),
    "Error fetching agents KPI",
  );

/**
 * Get Driver KPI
 */
export const getDriverKpi = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/DriverKpi"),
    "Error fetching driver KPI",
  );

/**
 * Get Controller KPI
 */
export const getControllerKpi = () =>
  safeReturn(
    () => axiosInstance.get("/Dashboard/ControllerKpi"),
    "Error fetching controller KPI",
  );
